import {
    SearchIndexEducator,
    SearchIndexCourseFilters,
    SearchIndexEducatorFilters,
    LegacySearchIndexCourse,
    SearchIndexAutoComplete,
} from '@shared/types';
import { GetServerSideProps } from 'next';
import { FunctionComponent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
    rawCourseSearch,
    rawSearchIndexCourseResult,
    SearchIndexCourseResult,
} from '../../services/search/course';
import { EducatorSearch, SearchIndexEducatorResult } from '../../services/search/educator';
import styles from './styles.module.scss';
import Link from 'next/link';
import Image from 'next/image';
import { CoursePrice } from '../../components/CoursePrice';
import CourseTagLine from '../../components/CourseTagline';
import CourseDeliveryDetails from '../../components/CourseDeliveryDetails';
import PaginationLinks from '../../components/PaginationLinks';
import { convert as html2PlainText, HtmlToTextOptions } from 'html-to-text';
import Button from '../../components/Button';
import BlueprintIcon from '../../components/BlueprintIcon';
import ButtonLink from '../../components/ButtonLink';
import { EarthAmericasRegularIcon } from '../../components/Icons';
import CoursesLayout from '../../layouts/CoursesLayout';
import Header from '../../pages_components/courses/Header';
import SearchPageFilters from '../../pages_components/courses/SearchPageFilters';
import useCourseSearch, {
    WebCourseSearchFilters,
    webFiltersToSearchFilters as courseWebFiltersToSearchFilters,
    useCourseSearchProps,
} from '../../services/search/course/useCourseSearch';
import useEducatorSearch, {
    WebEducatorSearchFilters,
    webFiltersToSearchFilters as educatorWebFiltersToSearchFilters,
} from '../../services/search/educator/useEducatorSearch';
import useRouterState, { UrlObject, useRouterStatePart } from '../../hooks/useRouterState';
import { ParsedUrlQuery } from 'node:querystring';
import classnames from 'classnames';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { preFetchMarketplaceTags } from '../../hooks/useMarketplaceTags';
import { preFetchCategories } from '../../hooks/useCategories';
import { categoryPath, tagPath, deliveryPath, locationRadiusPath } from '../../services/urlPath';
import { useCourseFilterName } from '../../services/search/course/useCourseFilterName';
import StarRatingWithCount from '../../components/StarRatingWithCount';
import Alert from '../../components/Alert';
import {
    DEFAULT_RADIUS,
    LocationNotFoundError,
    SearchRadius,
    findLocationByName,
} from '../../services/search/locationFilter';
import { CademyError, ICademyError } from '@shared/domain-shared';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { SearchIndexSortBy } from '../../services/search/shared';
import useCoursesSchema from '../../pages_components/courses/useCoursesSchema';
import { useIsSearchDebug } from '../../hooks/useIsSearchDebug';
import DebugSearchWeights from '../../pages_components/courses/DebugSearchWeights';
import { GeneralSearch } from '../../pages_components/home/SearchBar/PrimaryQuerySelect';
import SearchByLocation from '../../pages_components/courses/SearchByLocation';
import { parseDeliveryFilter } from '../../services/delivery';
import {
    SearchResultCourse,
    convertSearchIndexCourse,
} from '../../services/convertSearchIndexCourse';
import EllipsisIcon from '../../components/Icons/EllipsisIcon';
import { SaveableCourseOverlay } from '../../components/SaveableCourseOverlay';
import { useSavedCourse } from '../../hooks/useSavedCourse';
import { useSavedCoursesState } from '../../hooks/useSavedCoursesState';
const COURSES_PAGE_SLUG = 'courses/';

const html2PlainTextOptions: HtmlToTextOptions = {
    selectors: [{ selector: 'img', format: 'skip' }],
};

const PER_PAGE = 10;

export type EmptyResult = {
    results: [];
    page: 0;
    nextPage: 0;
    totalPages: 0;
    totalResults: 0;
};
export type SearchPageProps = {
    query: {
        search:
            | {
                  type: 'text';
                  term: string;
              }
            | {
                  type: 'category-or-tag';
                  slug: string;
              };
        filters: {
            courseFilters: WebCourseSearchFilters;
            educatorFilters: WebEducatorSearchFilters;
        };
        page: number;
        sort_by: SearchIndexSortBy;
        providers_only: boolean;
        debug: boolean;
    };
    categoryOrTagSearchText?: string;
    results: {
        courses: rawSearchIndexCourseResult | EmptyResult;
        coursesError: ICademyError | null;
        onlineAlternativeCourses: rawSearchIndexCourseResult | EmptyResult;
        onlineAlternativeCoursesError: ICademyError | null;
        educators: SearchIndexEducatorResult | EmptyResult;
        educatorsError: ICademyError | null;
    };
};

export type ParsedResults = {
    courses: SearchIndexCourseResult | EmptyResult;
    coursesError: CademyError | null;
    onlineAlternativeCourses: SearchIndexCourseResult | EmptyResult;
    onlineAlternativeCoursesError: CademyError | null;
    educators: SearchIndexEducatorResult | EmptyResult;
    educatorsError: CademyError | null;
};

type FilterSidebarProps = {
    children: ReactNode;
};

const FiltersSidebar: FunctionComponent<FilterSidebarProps> = ({ children }) => {
    return <div className={styles.FiltersSidebar}>{children}</div>;
};

type ShowAllButtonProps = {
    results: {
        courses: ParsedResults['courses'];
        educators: ParsedResults['educators'];
    };
    providers_only: boolean;
    setProvidersOnly: (newValue: boolean) => void;
    getChangeLink: (newValue: boolean) => UrlObject;
};

const ShowAllButton: FunctionComponent<ShowAllButtonProps> = ({
    results,
    providers_only,
    setProvidersOnly,
    getChangeLink,
}) => {
    const totalResults = useMemo(() => {
        return providers_only ? results.courses.totalResults : results.educators.totalResults;
    }, [results, providers_only]);

    return (
        <ButtonLink
            href={getChangeLink(!providers_only)}
            onClick={(event) => {
                event.preventDefault();
                setProvidersOnly(!providers_only);
            }}
        >
            Show all {totalResults}
        </ButtonLink>
    );
};

type SecondaryHeaderCardProps = {
    results: {
        courses: ParsedResults['courses'];
        educators: ParsedResults['educators'];
    };
    searchTerm: string;
    providers_only: boolean;
    setProvidersOnly: (newValue: boolean) => void;
    getChangeLink: (newValue: boolean) => UrlObject;
};
const SecondaryHeaderCard: FunctionComponent<SecondaryHeaderCardProps> = ({
    results,
    searchTerm,
    providers_only,
    setProvidersOnly,
    getChangeLink,
}) => {
    const resultsTitle = useMemo(() => {
        const type = providers_only ? 'Courses' : 'Educators';
        if (searchTerm) {
            return `${type} matching "${searchTerm}"`;
        } else {
            return type;
        }
    }, [providers_only, searchTerm]);

    return (
        <div className={styles.SecondaryHeaderCard}>
            <h2>{resultsTitle}</h2>
            <ShowAllButton
                results={results}
                providers_only={providers_only}
                setProvidersOnly={setProvidersOnly}
                getChangeLink={getChangeLink}
            />
        </div>
    );
};

const CourseCard = ({
    course,
    index,
    isSaved,
}: {
    course: LegacySearchIndexCourse;
    index: number;
    isSaved: boolean;
}) => {
    const isSearchDebug = useIsSearchDebug();
    const { onSave, onUnsave, saved, loginModal } = useSavedCourse({
        courseId: course._id,
        saved: isSaved,
    });

    return (
        <div className={styles.courseCardContainer}>
            {loginModal}
            <SaveableCourseOverlay isSaved={saved} onSaved={onSave} onUnsaved={onUnsave}>
                <div className={styles.courseCard}>
                    <div className={styles.courseCardTitle}>
                        {isSearchDebug ? (
                            <h2 style={{ color: 'red' }}>Score: {course.search_score}</h2>
                        ) : null}
                        <h2>{course.name}</h2>
                        {course.reviews && course.reviews.count > 0 ? (
                            <StarRatingWithCount
                                rating={course.reviews.rating}
                                count={course.reviews.count}
                            />
                        ) : null}
                        <h3>
                            By{' '}
                            <Link
                                className={styles.couresCardEduatorLink}
                                href={`/${course.educator_slug}/courses`}
                            >
                                {course.educator_name}
                            </Link>
                        </h3>
                    </div>
                    <div className={styles.courseCardDetails}>
                        <div>
                            <p>
                                {course.meta_description?.length
                                    ? course.meta_description
                                    : course.description_plaintext}
                            </p>
                        </div>
                    </div>
                    <div className={styles.courseCardImage}>
                        <Image
                            src={course.image_url}
                            alt={course.name}
                            fill
                            priority={index === 0}
                            sizes="(min-width: 510px) 224px, calc(100vw - 80px)"
                        />
                    </div>
                    <div className={styles.courseCardFooter}>
                        <div className={styles.courseCardFooterLeft}>
                            <CourseDeliveryDetails course={course} />
                            <CourseTagLine course={course} />
                        </div>
                        <div className={styles.courseCardFooterRight}>
                            <CoursePrice course={course} />
                        </div>
                    </div>
                </div>

                {/* This link is absolute positioned over the above content so that we don't end up nesting links inside links */}
                <Link
                    href={`/${course.educator_slug}/${course.slug}`}
                    className={styles.courseCardOverlayLink}
                    title={course.name}
                />
            </SaveableCourseOverlay>
        </div>
    );
};

const EducatorCard = ({ educator }: { educator: SearchIndexEducator }) => {
    const handleClick = useCallback(
        (url: string) => (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();

            if (typeof window !== 'undefined') {
                window.open(url, '_blank');
            }
        },
        []
    );

    const messageButton = useMemo(() => {
        if (educator.claimed || (educator.email_address && educator.can_be_contacted)) {
            return (
                <ButtonLink
                    href={`/${educator.slug}/enquiry`}
                    className={styles.messageEducatorButton}
                >
                    <BlueprintIcon icon="Envelope" /> Message
                </ButtonLink>
            );
        }

        return null;
    }, [educator]);

    const [imgSrc, setImgSrc] = useState(
        encodeURI(
            educator.logo_url ||
                `${process.env.NEXT_PUBLIC_ASSETS_URL}/images/educator_fallback_logo.svg`
        )
    );

    return (
        <div className={styles.educatorCard}>
            <Link href={`/${educator.slug}`}>
                <div className={styles.educatorCardImage}>
                    {/* <img src={`https://logo.clearbit.com/${educator.links?.web}`} height={75} width={75} /> */}
                    <Image
                        src={imgSrc}
                        alt={educator.name}
                        height={75}
                        width={75}
                        onError={() =>
                            setImgSrc(
                                `${process.env.NEXT_PUBLIC_ASSETS_URL}/images/educator_fallback_logo.svg`
                            )
                        }
                    />
                </div>
                <div className={styles.educatorCardDetails}>
                    <h2>{educator.name.toLowerCase()}</h2>
                    <div>
                        {educator.reviews && educator.reviews.count > 0 ? (
                            <StarRatingWithCount
                                rating={educator.reviews.rating}
                                count={educator.reviews.count}
                            />
                        ) : null}
                        {/* educator keyword */}
                        <h3>{educator.city}</h3>
                    </div>
                    <p>{html2PlainText(educator.bio || '', html2PlainTextOptions)}</p>
                </div>
            </Link>
            <div className={styles.educatorCardFooter}>
                <div className={styles.educatorCardFooterLeft}>
                    {messageButton}
                    {educator.phone_number ? (
                        <Button
                            onClick={handleClick(`tel:${educator.phone_number}`)}
                            name="Contact by phone"
                            aria-label="Contact by phone"
                        >
                            <BlueprintIcon icon="Phone" />
                        </Button>
                    ) : null}
                    {educator.links?.web ? (
                        <Button
                            onClick={handleClick(`${educator.links?.web}`)}
                            name={`See ${educator.name}'s website`}
                            aria-label={`See ${educator.name}'s website`}
                        >
                            <EarthAmericasRegularIcon />
                        </Button>
                    ) : null}
                </div>
                <div className={styles.educatorCardFooterRight}>
                    {/* <span>❤️ Follow</span> */}
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                            <Button icon={<EllipsisIcon />} />
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content
                                side="bottom"
                                align="end"
                                className={styles.educatorCardDropdown}
                            >
                                {educator.claimed !== true ? (
                                    <DropdownMenu.Item>
                                        <ButtonLink href={`/${educator.slug}/claim-profile`}>
                                            Claim profile
                                        </ButtonLink>
                                    </DropdownMenu.Item>
                                ) : null}
                                <DropdownMenu.Item>
                                    <ButtonLink
                                        href={`/suggest-an-edit?slug=${educator.slug}&name=${educator.name}`}
                                    >
                                        Suggest an Edit
                                    </ButtonLink>
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                </div>
            </div>
        </div>
    );
};

const CourseResults: FunctionComponent<{
    courseResult: SearchIndexCourseResult;
    savedCoursesState: Record<string, boolean>;
}> = ({ courseResult, savedCoursesState }) => {
    return (
        <div className={styles.searchResults}>
            {courseResult.results.map((course, index) => (
                <CourseCard
                    key={course._id}
                    course={course}
                    index={index}
                    isSaved={savedCoursesState[course._id]}
                />
            ))}
        </div>
    );
};

const EducatorResults: FunctionComponent<{ educatorResult: SearchIndexEducatorResult }> = ({
    educatorResult,
}) => {
    return (
        <div className={styles.searchResults}>
            {educatorResult.results.map((educator) => (
                <EducatorCard key={educator._id} educator={educator} />
            ))}
        </div>
    );
};

type AlternativeCourseResultsProps = {
    results: SearchIndexCourseResult['results'];
    totalResults: number;
    isLoading: boolean;
    error: string;
    filters: SearchPageProps['query']['filters'];
    setFilters: (newFilters: SearchPageProps['query']['filters']) => unknown;
    getFiltersChangeLink: (newFilters: SearchPageProps['query']['filters']) => UrlObject;
    savedCoursesState: Record<string, boolean>;
};
const AlternativeCourseResults: FunctionComponent<AlternativeCourseResultsProps> = ({
    results,
    totalResults,
    isLoading,
    error,
    filters,
    setFilters,
    getFiltersChangeLink,
    savedCoursesState,
}) => {
    const convertedCourseFilters = useMemo(() => {
        return convertToOnlineAlternativeFilters(filters.courseFilters);
    }, [filters]);

    const onClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            setFilters({ ...filters, courseFilters: convertedCourseFilters });
        },
        [filters, convertedCourseFilters, setFilters]
    );

    const href = useMemo(() => {
        return getFiltersChangeLink({ ...filters, courseFilters: convertedCourseFilters });
    }, [filters, convertedCourseFilters, getFiltersChangeLink]);

    if (totalResults === 0 || results.length === 0) {
        return null;
    }
    return (
        <div className={classnames({ [styles.isLoading]: isLoading })}>
            {error ? (
                <Alert preset="danger" className={styles.errorAlert}>
                    {error}
                </Alert>
            ) : null}
            <div className={styles.SecondaryHeaderCard}>
                <h2>Online Options</h2>
                <ButtonLink href={href} onClick={onClick}>
                    Show all {totalResults}
                </ButtonLink>
            </div>
            <div className={styles.searchResults}>
                {results.map((course, index) => (
                    <CourseCard
                        key={course._id}
                        course={course}
                        index={index}
                        isSaved={savedCoursesState[course._id]}
                    />
                ))}
            </div>
        </div>
    );
};

const ShowAllButtonContainer: FunctionComponent<ShowAllButtonProps> = ({
    results,
    providers_only,
    setProvidersOnly,
    getChangeLink,
}) => {
    if (results.educators.results.length < 5 && providers_only === false) {
        return null;
    }

    if (results.courses.results.length < 5 && providers_only === true) {
        return null;
    }

    return (
        <div className={styles.showAllButtonContainer}>
            <ShowAllButton
                results={results}
                providers_only={providers_only}
                setProvidersOnly={setProvidersOnly}
                getChangeLink={getChangeLink}
            />
        </div>
    );
};

const queryValueToStringArray = (value: ParsedUrlQuery[string]): Array<string> => {
    if (typeof value === 'string') {
        return [value];
    }
    if (Array.isArray(value)) {
        return value;
    }
    return [];
};

const queryValueToString = (value: ParsedUrlQuery[string]): string => {
    if (typeof value === 'string') {
        return value;
    }
    if (Array.isArray(value)) {
        return value[0];
    }
    return '';
};

const parseRadius = (query: ParsedUrlQuery): SearchRadius => {
    const radiusString = queryValueToString(query.radius);
    if (!radiusString) {
        return DEFAULT_RADIUS;
    }
    const [value, _unit] = radiusString.split('-');
    const numericValue = Number(value);
    if (isNaN(numericValue)) {
        return DEFAULT_RADIUS;
    }
    return {
        value: numericValue,
        unit: 'miles',
    };
};

const parseMinimumRating = (query: ParsedUrlQuery): SearchIndexCourseFilters['minimum_rating'] => {
    const ratingString = queryValueToString(query.minimum_rating);
    if (!ratingString) {
        return undefined;
    }
    const numericValue = Number(ratingString);
    if (isNaN(numericValue)) {
        return undefined;
    }
    if (numericValue > 5) {
        return 5;
    }
    if (numericValue < 1) {
        return 1;
    }
    return numericValue as SearchIndexCourseFilters['minimum_rating'];
};

const queryToFilters = (
    query: ParsedUrlQuery
): { courseFilters: WebCourseSearchFilters; educatorFilters: WebEducatorSearchFilters } => {
    const parts = queryValueToStringArray(query.parts).map((part) => {
        return part.replace(/~/g, '/');
    });

    const minimumRatingFilter = parseMinimumRating(query);
    const initialFilters: {
        courseFilters: WebCourseSearchFilters;
        educatorFilters: WebEducatorSearchFilters;
    } = {
        courseFilters: minimumRatingFilter
            ? { minimum_rating: minimumRatingFilter, context: 'marketplace' }
            : { context: 'marketplace' },
        educatorFilters: minimumRatingFilter
            ? { minimum_rating: minimumRatingFilter, context: 'marketplace' }
            : { context: 'marketplace' },
    };

    return parts.reduce(
        (
            {
                courseFilters,
                educatorFilters,
            }: { courseFilters: WebCourseSearchFilters; educatorFilters: WebEducatorSearchFilters },
            part
        ) => {
            if (!part) return { courseFilters, educatorFilters };

            const [key, ...splitValue] = part.split('-');
            const value = splitValue.join('-');

            switch (key) {
                case 'delivered': {
                    courseFilters['delivery'] = parseDeliveryFilter(value);
                    break;
                }
                case 'about':
                case 'with': {
                    courseFilters['marketplace_tag_slug'] = (
                        courseFilters.marketplace_tag_slug || []
                    ).concat(value);
                    educatorFilters['marketplace_tag_slug'] = (
                        educatorFilters.marketplace_tag_slug || []
                    ).concat(value);
                    break;
                }
                case 'in': {
                    const locationFilter: NonNullable<WebCourseSearchFilters['location']> = {
                        known_location:
                            findLocationByName(decodeURIComponent(value))?.name ||
                            decodeURIComponent(value),
                        radius: parseRadius(query),
                    };
                    courseFilters['location'] = locationFilter;
                    educatorFilters['location'] = locationFilter;
                    break;
                }
                case 'near': {
                    const locationFilter: NonNullable<WebCourseSearchFilters['location']> = {
                        query: decodeURIComponent(value),
                        radius: parseRadius(query),
                    };
                    courseFilters['location'] = locationFilter;
                    educatorFilters['location'] = locationFilter;
                    break;
                }
                case 'category': {
                    courseFilters['category_slug'] = (courseFilters.category_slug || []).concat(
                        value
                    );
                    educatorFilters['category_slug'] = (educatorFilters.category_slug || []).concat(
                        value
                    );
                    break;
                }
            }
            return { courseFilters, educatorFilters };
        },
        initialFilters
    );
};

const filtersToPath = (
    courseFilters: WebCourseSearchFilters,
    search: SearchPageProps['query']['search']
): string => {
    return [
        ...(search.type === 'category-or-tag' ? [search.slug] : []),
        ...(courseFilters.category_slug || []).map((slug) => categoryPath(slug)),
        ...(courseFilters.marketplace_tag_slug || []).map((slug) => tagPath(slug)),
        courseFilters.delivery ? deliveryPath(courseFilters.delivery) : undefined,
        courseFilters.location ? locationRadiusPath(courseFilters.location) : undefined,
    ]
        .filter((part): part is string => part !== undefined)
        .map((part) => part.replace(/%2F/g, '~'))
        .join('/');
};

const queryToSearch = (query: ParsedUrlQuery): SearchPageProps['query']['search'] => {
    const categoryOrTagSearchSlug = queryValueToStringArray(query.parts).find((part) => {
        const [key] = part.split('-');
        return ['delivered', 'about', 'with', 'in', 'near', 'category'].includes(key) === false;
    });
    if (categoryOrTagSearchSlug) {
        return {
            type: 'category-or-tag',
            slug: categoryOrTagSearchSlug,
        };
    }
    return {
        type: 'text',
        term: queryValueToString(query.q),
    };
};

const queryToPage = (query: ParsedUrlQuery): number => {
    if (typeof query.page === 'string') {
        return parseInt(query.page);
    }
    if (Array.isArray(query.page)) {
        return parseInt(query.page[0]);
    }
    return 1;
};

const parseSortFromQuerystringValue = (
    queryStringValue: string | Array<string> | undefined
): SearchIndexSortBy => {
    if (!queryStringValue) {
        return 'relevance';
    }
    if (Array.isArray(queryStringValue)) {
        return parseSortFromQuerystringValue(queryStringValue[0]);
    }
    switch (queryStringValue) {
        case 'relevance':
            return 'relevance';
        case 'price_asc':
            return 'price_asc';
        case 'price_desc':
            return 'price_desc';
        case 'rating_desc':
            return 'rating_desc';
    }
    return 'relevance';
};

const queryToSort = (query: ParsedUrlQuery): SearchIndexSortBy => {
    return parseSortFromQuerystringValue(query.sort_by);
};

const queryToProvidersOnly = (query: ParsedUrlQuery): boolean => {
    return query.providers_only === 'true';
};

const removeEmpty = (obj: ParsedUrlQuery): ParsedUrlQuery => {
    return Object.entries(obj).reduce((obj, [key, val]) => {
        if (!val) {
            return obj;
        }
        return { ...obj, [key]: val };
    }, {});
};

const stateToUrlObject = (state: SearchPageProps['query']): UrlObject => {
    const minimumRatingFilter =
        state.filters.courseFilters.minimum_rating || state.filters.educatorFilters.minimum_rating;
    return {
        pathname: filtersToPath(state.filters.courseFilters, state.search),
        query: removeEmpty({
            q: state.search.type === 'text' ? state.search.term : undefined,
            debug: state.debug === true ? 'true' : undefined,
            page: state.page !== 1 ? String(state.page) : undefined,
            sort_by: state.sort_by !== 'relevance' ? state.sort_by : undefined,
            providers_only: state.providers_only ? 'true' : undefined,
            radius:
                state.filters.courseFilters.location &&
                state.filters.courseFilters.location.radius.value !== DEFAULT_RADIUS.value
                    ? `${state.filters.courseFilters.location.radius.value}-${state.filters.courseFilters.location.radius.unit}`
                    : undefined,
            minimum_rating: minimumRatingFilter ? String(minimumRatingFilter) : undefined,
        }),
    };
};

const urlObjectToState = (urlObject: UrlObject): SearchPageProps['query'] => {
    return {
        search: queryToSearch(urlObject.query),
        filters: queryToFilters(urlObject.query),
        page: queryToPage(urlObject.query),
        sort_by: queryToSort(urlObject.query),
        providers_only: queryToProvidersOnly(urlObject.query),
        debug: urlObject.query.debug === 'true',
    };
};

const parseRawError = (rawError: ICademyError | null): CademyError | null => {
    if (rawError === null) {
        return rawError;
    }
    if (rawError instanceof CademyError) {
        return rawError;
    }
    return CademyError.fromObject(rawError);
};

const parseRawResults = (
    rawResults: SearchPageProps['results'],
    filters: SearchIndexCourseFilters
): ParsedResults => {
    return {
        courses: {
            ...rawResults.courses,
            results: rawResults.courses.results.map(convertSearchIndexCourse(filters)),
        },
        coursesError: parseRawError(rawResults.coursesError),
        onlineAlternativeCourses: {
            ...rawResults.onlineAlternativeCourses,
            results: rawResults.onlineAlternativeCourses.results.map(
                convertSearchIndexCourse(filters)
            ),
        },
        onlineAlternativeCoursesError: parseRawError(rawResults.onlineAlternativeCoursesError),
        educators: rawResults.educators,
        educatorsError: parseRawError(rawResults.educatorsError),
    };
};

type UseCanonicalPathProps = {
    categoryOrTagSearchText?: string;
    state: SearchPageProps['query'];
};

/**
 * If the category or tag does not exist then it MUST NOT exist
 * in the canonical path as the search results will fallback
 * to the location and/or delivery only query but with a
 * unique pathname which would be a duplicate page.
 */
const useCanonicalPath = ({ state, categoryOrTagSearchText }: UseCanonicalPathProps) => {
    return useMemo(() => {
        if (!categoryOrTagSearchText || categoryOrTagSearchText === '') {
            return (
                COURSES_PAGE_SLUG +
                stateToUrlObject({ ...state, ...{ search: { type: 'text', term: '' } } }).pathname
            );
        }

        return COURSES_PAGE_SLUG + stateToUrlObject(state).pathname;
    }, [state, categoryOrTagSearchText]);
};

type UseAlternativeCourseSearchProps = useCourseSearchProps & {
    primaryResults: SearchResultCourse[];
};
const useAlternativeCourseSearch = ({
    primaryResults,
    ...useCourseSearchProps
}: UseAlternativeCourseSearchProps) => {
    const {
        results: alternativeCourseResults,
        totalResults: alternativeCourseTotalResults,
        isLoading: alternativeCourseIsLoading,
        error: alternativeCourseSearchError,
    } = useCourseSearch(useCourseSearchProps);

    const primaryCourseIds = useMemo<string[]>(() => {
        return primaryResults.map((result) => result._id);
    }, [primaryResults]);

    const dedupedAlternativeCourseResults = useMemo(() => {
        return alternativeCourseResults.filter((result) => !primaryCourseIds.includes(result._id));
    }, [alternativeCourseResults, primaryCourseIds]);

    return {
        alternativeCourseResults: dedupedAlternativeCourseResults,
        alternativeCourseTotalResults,
        alternativeCourseIsLoading,
        alternativeCourseSearchError,
    };
};

export const SearchPage: FunctionComponent<SearchPageProps> = ({
    query,
    results: rawResults,
    categoryOrTagSearchText: initialCategoryOrTagSearchText,
}) => {
    const results = useMemo(() => {
        return parseRawResults(rawResults, query.filters.courseFilters);
    }, [rawResults, query]);

    const [routerState, setRouterState, getRouterStateChangeLink] = useRouterState<
        SearchPageProps['query']
    >({
        initialValue: query,
        mapper: stateToUrlObject,
        parser: urlObjectToState,
    });

    const [filters, _setFilters, getFiltersChangeLink] = useRouterStatePart(
        routerState,
        setRouterState,
        getRouterStateChangeLink,
        'filters'
    );
    const [search] = useRouterStatePart(
        routerState,
        setRouterState,
        getRouterStateChangeLink,
        'search'
    );
    const [providersOnly, setProvidersOnly, getProvidersOnlyChangeLink] = useRouterStatePart(
        routerState,
        setRouterState,
        getRouterStateChangeLink,
        'providers_only'
    );
    const [page, setPage, getPageLink] = useRouterStatePart(
        routerState,
        setRouterState,
        getRouterStateChangeLink,
        'page'
    );
    const [sortBy, setSortBy] = useRouterStatePart(
        routerState,
        setRouterState,
        getRouterStateChangeLink,
        'sort_by'
    );

    const [categoryOrTagSearchText, setCategoryOrTagSearchText] = useState(
        initialCategoryOrTagSearchText
    );

    useEffect(() => {
        setCategoryOrTagSearchText(initialCategoryOrTagSearchText);
    }, [initialCategoryOrTagSearchText]);

    const searchTerm = useMemo(() => {
        if (search.type === 'category-or-tag') {
            return categoryOrTagSearchText;
        }
        return search.term;
    }, [categoryOrTagSearchText, search]);

    const setFilters = useCallback(
        (newValue: {
            courseFilters: WebCourseSearchFilters;
            educatorFilters: WebEducatorSearchFilters;
        }) => {
            const filters = {
                courseFilters: Object.entries(newValue.courseFilters).reduce(
                    (filters: SearchIndexCourseFilters, [key, value]) => {
                        if (Array.isArray(value) && value.length === 0) {
                            return filters;
                        }
                        return { ...filters, [key]: value };
                    },
                    { context: 'marketplace' }
                ),
                educatorFilters: Object.entries(newValue.educatorFilters).reduce(
                    (filters: SearchIndexEducatorFilters, [key, value]) => {
                        if (Array.isArray(value) && value.length === 0) {
                            return filters;
                        }
                        return { ...filters, [key]: value };
                    },
                    { context: 'marketplace' }
                ),
            };
            setRouterState({ ...routerState, filters, page: 1 });
        },
        [routerState, setRouterState]
    );

    const setSearchAndResetPage = useCallback(
        (search: SearchIndexAutoComplete | GeneralSearch) => {
            if (search.type === 'category' || search.type === 'tag') {
                setCategoryOrTagSearchText(search.name);
                return setRouterState({
                    ...routerState,
                    search: {
                        type: 'category-or-tag',
                        slug: search.slug,
                    },
                    page: 1,
                });
            }
            setCategoryOrTagSearchText('');
            return setRouterState({
                ...routerState,
                search: {
                    type: 'text',
                    term: search.name,
                },
                page: 1,
            });
        },
        [routerState, setRouterState]
    );

    const {
        results: courseResults,
        nextPage: courseNextPage,
        page: coursePage,
        totalResults: courseTotalResults,
        isLoading: courseIsLoading,
        setPage: courseSetPage,
        error: courseSearchError,
    } = useCourseSearch({
        filters: filters.courseFilters,
        search: searchTerm || '',
        sort: sortBy,
        initialResults: results.courses,
        initialError: results.coursesError,
        debug: routerState.debug,
        perPage: PER_PAGE,
    });

    const courseSearchErrorText = useMemo(() => {
        if (!courseSearchError) {
            return '';
        }
        if (courseSearchError instanceof CademyError) {
            return courseSearchError.detail || courseSearchError.title;
        }
        return courseSearchError;
    }, [courseSearchError]);

    useEffect(() => {
        if (providersOnly === true && coursePage !== 1) {
            courseSetPage(1);
            return;
        }
        if (providersOnly === false && page !== coursePage) {
            courseSetPage(page);
        }
    }, [page, coursePage, courseSetPage, providersOnly]);

    const courses = useMemo((): SearchIndexCourseResult => {
        return {
            nextPage: courseNextPage,
            page: coursePage,
            results: courseResults,
            totalPages: Math.ceil(courseTotalResults / PER_PAGE),
            totalResults: courseTotalResults,
        };
    }, [courseNextPage, coursePage, courseResults, courseTotalResults]);

    const {
        alternativeCourseResults,
        alternativeCourseTotalResults,
        alternativeCourseIsLoading,
        alternativeCourseSearchError,
    } = useAlternativeCourseSearch({
        primaryResults: courseResults,
        filters: convertToOnlineAlternativeFilters(filters.courseFilters),
        search: searchTerm || '',
        sort: sortBy,
        initialResults: results.onlineAlternativeCourses,
        initialError: results.onlineAlternativeCoursesError,
        debug: routerState.debug,
        perPage: PER_PAGE,
    });

    const alternativeCourseSearchErrorText = useMemo(() => {
        if (!alternativeCourseSearchError) {
            return '';
        }
        if (alternativeCourseSearchError instanceof CademyError) {
            return alternativeCourseSearchError.detail || alternativeCourseSearchError.title;
        }
        return alternativeCourseSearchError;
    }, [alternativeCourseSearchError]);

    const onlineAlternativeCourses = useMemo((): SearchIndexCourseResult => {
        return {
            nextPage: 1,
            page: 1,
            results: alternativeCourseResults,
            totalPages: 1,
            totalResults: alternativeCourseTotalResults,
        };
    }, [alternativeCourseResults, alternativeCourseTotalResults]);

    const {
        results: educatorResults,
        nextPage: educatorNextPage,
        page: educatorPage,
        totalResults: educatorTotalResults,
        isLoading: educatorIsLoading,
        setPage: educatorSetPage,
        error: educatorSearchError,
    } = useEducatorSearch({
        filters: filters.educatorFilters,
        search: searchTerm || '',
        sort: sortBy,
        initialResults: results.educators,
        initialError: results.educatorsError,
    });

    const educatorSearchErrorText = useMemo(() => {
        if (!educatorSearchError) {
            return '';
        }
        if (educatorSearchError instanceof CademyError) {
            return educatorSearchError.detail || educatorSearchError.title;
        }
        return educatorSearchError;
    }, [educatorSearchError]);

    useEffect(() => {
        if (providersOnly === false && educatorPage !== 1) {
            educatorSetPage(1);
            return;
        }
        if (providersOnly === true && page !== educatorPage) {
            educatorSetPage(page);
        }
    }, [page, educatorPage, educatorSetPage, providersOnly]);

    const educators = useMemo((): SearchIndexEducatorResult => {
        return {
            nextPage: educatorNextPage,
            page: educatorPage,
            results: educatorResults,
            totalPages: Math.ceil(educatorTotalResults / PER_PAGE),
            totalResults: educatorTotalResults,
        };
    }, [educatorNextPage, educatorPage, educatorResults, educatorTotalResults]);

    const primaryResult = useMemo(() => {
        return providersOnly ? educators : courses;
    }, [educators, courses, providersOnly]);

    const secondaryResult = useMemo(() => {
        return providersOnly ? courses : educators;
    }, [educators, courses, providersOnly]);

    const courseFilterName = useCourseFilterName(filters.courseFilters, categoryOrTagSearchText);
    const title = `${courseFilterName} - Cademy`;
    const description = `Search results for ${courseFilterName} as well as relevant educators on ${process.env.NEXT_PUBLIC_DOMAIN_NAME}`;
    const path = COURSES_PAGE_SLUG + (stateToUrlObject(routerState).pathname ?? undefined);
    const canonicalPath = useCanonicalPath({ state: routerState, categoryOrTagSearchText });

    const resultHeaderTitle = useMemo(() => {
        if (providersOnly) {
            return `Educators providing ${courseFilterName}`;
        }
        return courseFilterName;
    }, [courseFilterName, providersOnly]);

    const hasInvalidLocation = useMemo(() => {
        return (
            (courseSearchError && courseSearchError instanceof LocationNotFoundError) ||
            (educatorSearchError && educatorSearchError instanceof LocationNotFoundError) ||
            false
        );
    }, [courseSearchError, educatorSearchError]);

    const filtersContent = useMemo(() => {
        return (
            <SearchPageFilters
                results={{ courses, educators }}
                providers_only={providersOnly}
                search={searchTerm || ''}
                setSearch={setSearchAndResetPage}
                hasInvalidLocation={hasInvalidLocation}
                filters={filters}
                setFilters={setFilters}
                getFilterLink={getFiltersChangeLink}
            />
        );
    }, [
        courses,
        educators,
        providersOnly,
        searchTerm,
        setSearchAndResetPage,
        hasInvalidLocation,
        filters,
        setFilters,
        getFiltersChangeLink,
    ]);

    const setProvidersOnlyAndScroll = useCallback(
        (newValue: boolean) => {
            setProvidersOnly(newValue);
            window.scroll({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });
        },
        [setProvidersOnly]
    );
    const schema = useCoursesSchema(courses.results, filters.courseFilters);

    const showRelatedSearchOptions = useMemo(() => {
        if (['online', 'on-demand'].some((onlineOnlyOption) => path.includes(onlineOnlyOption))) {
            return null;
        }
        return (
            <div className={styles.relatedSearchesContainer}>
                <SearchByLocation
                    searchTerm={searchTerm || ''}
                    filters={filters}
                    getFilterLink={getFiltersChangeLink}
                />
            </div>
        );
    }, [filters, getFiltersChangeLink, path, searchTerm]);

    const noIndex = useMemo(() => {
        if (
            primaryResult.results.length === 0 &&
            routerState.page !== 1 &&
            routerState.page !== primaryResult.totalPages
        ) {
            return true;
        }

        return false;
    }, [primaryResult, routerState]);

    const { data: savedCoursesState } = useSavedCoursesState({
        courseIds: results.courses.results.map((course) => course._id),
    });

    return (
        <CoursesLayout
            title={title}
            description={description}
            path={path}
            schema={schema}
            noIndex={noIndex}
            page={page}
            canonicalPath={canonicalPath}
        >
            <div className={styles.searchPage}>
                <div className={styles.searchContainer}>
                    <FiltersSidebar>{filtersContent}</FiltersSidebar>
                    <div
                        className={classnames({
                            [styles.isLoading]: providersOnly ? educatorIsLoading : courseIsLoading,
                        })}
                    >
                        <Header
                            title={resultHeaderTitle}
                            filtersContent={filtersContent}
                            filters={filters}
                            setFilters={setFilters}
                            getFilterLink={getFiltersChangeLink}
                            results={{ courses, educators, onlineAlternativeCourses }}
                            query={query}
                            providers_only={providersOnly}
                            setSearch={setSearchAndResetPage}
                            search={searchTerm || ''}
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                        />
                        {routerState.debug === true ? <DebugSearchWeights /> : null}
                        {providersOnly === false && courseSearchError ? (
                            <Alert preset="danger" className={styles.errorAlert}>
                                {courseSearchErrorText}
                            </Alert>
                        ) : null}
                        {providersOnly === true && educatorSearchError ? (
                            <Alert preset="danger" className={styles.errorAlert}>
                                {educatorSearchErrorText}
                            </Alert>
                        ) : null}
                        {primaryResult.totalPages === 0 ? (
                            <PaginationLinks
                                currentPage={page}
                                setPage={setPage}
                                getPageLink={getPageLink}
                                totalPages={primaryResult.totalPages}
                            />
                        ) : null}
                        {courses.results.length > 0 && providersOnly === false ? (
                            <CourseResults
                                courseResult={courses}
                                savedCoursesState={savedCoursesState}
                            />
                        ) : null}
                        {courses.results.length < 5 && providersOnly === false ? (
                            <AlternativeCourseResults
                                isLoading={alternativeCourseIsLoading}
                                totalResults={alternativeCourseTotalResults}
                                results={alternativeCourseResults}
                                filters={filters}
                                setFilters={setFilters}
                                getFiltersChangeLink={getFiltersChangeLink}
                                error={alternativeCourseSearchErrorText}
                                savedCoursesState={savedCoursesState}
                            />
                        ) : null}
                        {educators.results.length > 0 && providersOnly === true ? (
                            <EducatorResults educatorResult={educators} />
                        ) : null}
                        {primaryResult.totalPages > 1 ? (
                            <PaginationLinks
                                currentPage={page}
                                setPage={setPage}
                                getPageLink={getPageLink}
                                totalPages={primaryResult.totalPages}
                            />
                        ) : null}
                    </div>
                    {secondaryResult.totalResults > 0 &&
                    (routerState.page === 1 || routerState.page === primaryResult.totalPages) ? (
                        <div
                            className={classnames({
                                [styles.isLoading]: providersOnly
                                    ? courseIsLoading
                                    : educatorIsLoading,
                            })}
                        >
                            <SecondaryHeaderCard
                                searchTerm={searchTerm || ''}
                                results={{ courses, educators }}
                                providers_only={providersOnly}
                                setProvidersOnly={setProvidersOnlyAndScroll}
                                getChangeLink={getProvidersOnlyChangeLink}
                            />
                            {providersOnly === true && courseSearchError ? (
                                <Alert preset="danger" className={styles.errorAlert}>
                                    {courseSearchErrorText}
                                </Alert>
                            ) : null}
                            {providersOnly === false && educatorSearchError ? (
                                <Alert preset="danger" className={styles.errorAlert}>
                                    {educatorSearchErrorText}
                                </Alert>
                            ) : null}
                            {courses.results.length > 0 && providersOnly === true ? (
                                <CourseResults
                                    courseResult={courses}
                                    savedCoursesState={savedCoursesState}
                                />
                            ) : null}
                            {educators.results.length > 0 && providersOnly === false ? (
                                <EducatorResults educatorResult={educators} />
                            ) : null}
                            <ShowAllButtonContainer
                                results={{ courses, educators }}
                                providers_only={providersOnly}
                                setProvidersOnly={setProvidersOnlyAndScroll}
                                getChangeLink={getProvidersOnlyChangeLink}
                            />
                        </div>
                    ) : null}
                    {showRelatedSearchOptions}
                </div>
            </div>
        </CoursesLayout>
    );
};

const getCategoryName = async (categorySlug: string): Promise<string> => {
    const response = await fetch(`${process.env.NEXT_PUBLIC_CADEMY_API_URL}/categories/bulk`, {
        method: 'POST',
        body: JSON.stringify({ category_slugs: [categorySlug], category_refs: [] }),
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });
    if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
    }
    const categories = await response.json();
    return categories[0]?.name || '';
};

const getTagName = async (tagSlug: string): Promise<string> => {
    const response = await fetch(
        `${process.env.NEXT_PUBLIC_CADEMY_API_URL}/marketplace_tags/bulk`,
        {
            method: 'POST',
            body: JSON.stringify({ tag_slugs: [tagSlug], tag_refs: [] }),
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        }
    );
    if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
    }
    const tags = await response.json();
    return tags[0]?.name || '';
};

const getCategoryOrTagName = async (categoryOrTagSlug: string): Promise<string> => {
    const [categoryName, tagName] = await Promise.all([
        getCategoryName(categoryOrTagSlug),
        getTagName(categoryOrTagSlug),
    ]);
    return categoryName || tagName || '';
};

const convertToOnlineAlternativeFilters = (
    filters: WebCourseSearchFilters
): WebCourseSearchFilters => {
    return {
        ...filters,
        delivery: 'online',
        location: undefined,
    };
};

export const getServerSideProps: GetServerSideProps<SearchPageProps> = async (context) => {
    const queryClient = new QueryClient();

    const filters = queryToFilters(context.query);

    const search = queryToSearch(context.query) || '';
    const page = context.query.page ? parseInt(context.query.page as string) : 1;
    const sort_by = parseSortFromQuerystringValue(context.query.sort_by);
    const providers_only = context.query.providers_only === 'true';
    const debug = context.query.debug === 'true';

    const [
        convertedCoursefilters,
        convertedEducatorFilters,
        onlineAlternativeFilters,
        categoryOrTagSearchText,
    ] = await Promise.all([
        courseWebFiltersToSearchFilters(filters.courseFilters),
        educatorWebFiltersToSearchFilters(filters.educatorFilters),
        courseWebFiltersToSearchFilters(convertToOnlineAlternativeFilters(filters.courseFilters)),
        search.type === 'category-or-tag' ? getCategoryOrTagName(search.slug) : '',
    ]);

    const searchTerm = search.type === 'category-or-tag' ? categoryOrTagSearchText : search.term;

    try {
        const emptyCourseResult: rawSearchIndexCourseResult = {
            results: [],
            page: 0,
            nextPage: 0,
            totalPages: 0,
            totalResults: 0,
        };
        const emptyEducatorResult: SearchIndexEducatorResult = {
            results: [],
            page: 0,
            nextPage: 0,
            totalPages: 0,
            totalResults: 0,
        };

        const getResults = async (): Promise<SearchPageProps['results']> => {
            try {
                if (providers_only === false) {
                    const [courseResult, onlineAlternativeResults, educatorSearchResult] =
                        await Promise.all([
                            rawCourseSearch(
                                searchTerm,
                                convertedCoursefilters,
                                page,
                                PER_PAGE,
                                sort_by,
                                debug
                            ),
                            rawCourseSearch(
                                searchTerm,
                                onlineAlternativeFilters,
                                1,
                                PER_PAGE,
                                sort_by,
                                debug
                            ),
                            EducatorSearch(searchTerm, convertedEducatorFilters, 1, PER_PAGE),
                        ]);

                    // Only return educators on the first page or the last page
                    const firstPage = page === 1;
                    const lastPage = page === courseResult.totalPages;
                    const noCourseResults =
                        providers_only === false && courseResult.totalResults === 0;

                    const educatorResult =
                        firstPage || lastPage || noCourseResults
                            ? educatorSearchResult
                            : emptyEducatorResult;
                    return {
                        courses: courseResult,
                        coursesError: null,
                        onlineAlternativeCourses: onlineAlternativeResults,
                        onlineAlternativeCoursesError: null,
                        educators: educatorResult,
                        educatorsError: null,
                    };
                } else {
                    const [educatorResult, courseSearchResult] = await Promise.all([
                        EducatorSearch(
                            searchTerm,
                            convertedEducatorFilters,
                            page,
                            PER_PAGE,
                            sort_by
                        ),
                        rawCourseSearch(
                            searchTerm,
                            convertedCoursefilters,
                            1,
                            PER_PAGE,
                            'relevance',
                            debug
                        ),
                    ]);
                    // Only return educators on the first page or the last page
                    const firstPage = page === 1;
                    const lastPage = page === educatorResult.totalPages;
                    const noEducatorResults =
                        providers_only === true && educatorResult.totalResults === 0;
                    const courseResult =
                        firstPage || lastPage || noEducatorResults
                            ? courseSearchResult
                            : emptyCourseResult;
                    return {
                        courses: courseResult,
                        coursesError: null,
                        onlineAlternativeCourses: emptyCourseResult,
                        onlineAlternativeCoursesError: null,
                        educators: educatorResult,
                        educatorsError: null,
                    };
                }
            } catch (error) {
                console.error(error);
                const errorToSet = JSON.parse(JSON.stringify(CademyError.fromUnknown(error)));
                return {
                    courses: emptyCourseResult,
                    coursesError: errorToSet,
                    onlineAlternativeCourses: emptyCourseResult,
                    onlineAlternativeCoursesError: errorToSet,
                    educators: emptyEducatorResult,
                    educatorsError: errorToSet,
                };
            }
        };

        const courseTagSlugs = filters.courseFilters.marketplace_tag_slug || [];
        const courseCategorySlugs = filters.courseFilters.category_slug || [];
        const [results] = await Promise.all([
            getResults(),
            preFetchMarketplaceTags(queryClient, {
                tag_refs: [],
                tag_slugs: courseTagSlugs,
            }),
            preFetchCategories(queryClient, {
                category_refs: [],
                category_slugs: courseCategorySlugs,
            }),
        ]);

        return {
            props: {
                query: {
                    search: search,
                    filters: filters,
                    page: page,
                    sort_by: sort_by,
                    providers_only: providers_only,
                    debug: debug,
                },
                categoryOrTagSearchText: categoryOrTagSearchText,
                results: results,
                dehydratedState: dehydrate(queryClient),
            },
        };
    } catch (error) {
        console.error(error);
        if (error instanceof Error) {
            if (error.message === '404 - Not found') {
                return {
                    notFound: true,
                };
            }
        }
        throw error;
    }
};

export default SearchPage;
